import React, { useState, useEffect } from "react";
import {Avatar,Button,Grid,TextField,Typography,Box,Alert, AlertColor, IconButton} from "@mui/material";
import { UserProfile } from "../../data/userProfile";
import { userSocialMediaLink } from "../../data/userSocialMediaLink";
import { SocialMediaPlatformInfo } from "../../data/socialMediaData";
import IconList from "../../data/account/socialMediaIcons";
import { saveUser } from "../../services/UserInfo";
import Editor from "../../utils/RTEditor";
import "./UserInfoForm.css";
import CheckIcon from "@mui/icons-material/Check";
import ErrorIcon from "@mui/icons-material/Error";
import EditIcon from '@mui/icons-material/Edit';
import { useGlobal } from "../../utils/GlobalContext";
import { getAuthUserProfile } from "../../services/auth/AuthServices";
import { FormControlLabel, Checkbox } from "@mui/material";

interface UserInfoFormProps {
  CurrentId: string;
  userInfo: UserProfile;
  socialMediaInfo: SocialMediaPlatformInfo[];
  onUserInfoUpdate: () => void;
}

const UserInfoForm: React.FC<UserInfoFormProps> = ({
  userInfo,
  socialMediaInfo,
  CurrentId,
  onUserInfoUpdate,
}) => {
  useEffect(() => {
    setFirstName(userInfo?.FirstName || "");
    setLastName(userInfo?.LastName || "");
    setEmail(userInfo?.Email || "");
    setBiography(userInfo?.Biography || "");
    setPictureLink(userInfo?.PictureLink || "");
    setSocialMediaLinks(userInfo?.SocialMediaLinks || []);
    setIsSubscribed(userInfo?.IsSubscribed ?? true);
  }, [userInfo]);

  const [fieldErrors, setFieldErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [alertType, setAlertType] = useState<AlertColor>('success');
  const [alertMessage, setAlertMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [activeSocialMediaId, setActiveSocialMediaId] = useState<string | null>(
    null
  );
  const handleIconClick = (platformId: string) => {
    setActiveSocialMediaId((prevId) =>
      prevId === platformId ? null : platformId
    );
  };
  const { state, dispatch } = useGlobal();
  const [showPictureLinkField, setShowPictureLinkField] = useState(false);
  const [firstName, setFirstName] = useState(userInfo?.FirstName || "");
  const [lastName, setLastName] = useState(userInfo?.LastName || "");
  const [email, setEmail] = useState(userInfo?.Email || "");
  const [biography, setBiography] = useState(userInfo?.Biography || "");
  const [pictureLink, setPictureLink] = useState(userInfo?.PictureLink || "");
  const [socialMediaLinks, setSocialMediaLinks] = useState<
    userSocialMediaLink[]
  >(userInfo?.SocialMediaLinks || []);
  const [isSubscribed, setIsSubscribed] = useState(userInfo?.IsSubscribed ?? true);

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    setter: React.Dispatch<React.SetStateAction<string>>
  ) => {
    const { value } = event.target;
    setter(value);
  };

  const handleEditorSave = (editorContent: string) => {
    setBiography(editorContent);
  };

  const handleSocialMediaLinkChange = (platformId: string, link: string) => {
    setSocialMediaLinks((prevLinks) => {
      const index = prevLinks.findIndex(
        (l) => l.SocialMediaPlatformId === platformId
      );
      if (index >= 0) {
        prevLinks[index] = { ...prevLinks[index], SocialMediaLink: link };
      } else {
        prevLinks.push({
          SocialMediaPlatformId: platformId,
          SocialMediaLink: link,
        });
      }
      return [...prevLinks];
    });
  };

  useEffect(() => {
    if (userInfo.SocialMediaLinks) {
      const links = userInfo.SocialMediaLinks.map((link) => ({
        SocialMediaPlatformId: link.SocialMediaPlatformId,
        SocialMediaLink: link.SocialMediaLink,
      }));
      setSocialMediaLinks(links);
    }
  }, [userInfo]);

  const validateForm = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const errors = {
      firstName: firstName ? "" : "First Name is required",
      lastName: lastName ? "" : "Last Name is required",
      email: email
        ? emailRegex.test(email)
          ? ""
          : "Invalid email format"
        : "Email is required",
    };
    setFieldErrors(errors);
    return !Object.values(errors).some((error) => error);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const socialMediaLinksChanged = () => {
      if (
        !userInfo.SocialMediaLinks ||
        userInfo.SocialMediaLinks.length !== socialMediaLinks.length
      ) {
        return true;
      }

      for (let i = 0; i < userInfo.SocialMediaLinks.length; i++) {
        if (
          userInfo.SocialMediaLinks[i].SocialMediaLink !==
          socialMediaLinks[i].SocialMediaLink
        ) {
          return true;
        }
      }
      return false;
    };

    const hasChanges =
        userInfo.FirstName !== firstName ||
        userInfo.LastName !== lastName ||
        userInfo.Email !== email ||
        userInfo.PictureLink !== pictureLink ||
        userInfo.Biography !== biography ||
        socialMediaLinksChanged() ||
        userInfo.IsSubscribed !== isSubscribed;

    if (!hasChanges) {
      console.log("No changes detected, not submitting.");
      return;
    }
    let payload = {};
      payload = {
        ...userInfo,
        Id: CurrentId,
        FirstName: firstName,
        LastName: lastName,
        Biography: biography,
        Email: email,
        PictureLink: pictureLink,
        SocialMediaLinks: socialMediaLinks,
        IsSubscribed: isSubscribed,
      };

    try {
      if (validateForm()) {
        await saveUser(JSON.stringify(payload));
        const data = await getAuthUserProfile();
        if(data!==null){
            dispatch({ type: 'SET_USERPROFILE', UserProfile: data });
        }
        if (CurrentId){
        onUserInfoUpdate();
        setAlertType('success');
        setAlertMessage('User info successfully saved.');
        setShowAlert(true);
        }
      }
    } catch (error) {
      console.error("Failed to save user info:", error);
      setAlertType('error');
      setAlertMessage('Failed to save user info. Please try again.');
      setShowAlert(true);
    }
    finally {
      setTimeout(() => {
        setShowAlert(false);
      }, 5000);
    }
  };

  return (
    <div className="ParentStyle">
      <Box>
      {showAlert && (
          <Alert
            icon={alertType === 'success' ? <CheckIcon fontSize="inherit" /> : <ErrorIcon fontSize="inherit" />}
            severity={alertType}
          >
            {alertMessage}
          </Alert>
        )}
        <Typography variant="h6" component="h2" textAlign="center">
          User Profile
        </Typography>
        <form onSubmit={handleSubmit}>
        <Grid 
        container 
        justifyContent="center" 
        alignItems="center" 
        spacing={2}
        >
            <Grid item>
              <Box position="relative" display="inline-flex">
                <Avatar
                  alt="User Avatar"
                  src={userInfo?.PictureLink || "https://creazilla-store.fra1.digitaloceanspaces.com/icons/3220761/user-circle-icon-md.png"}
                  className="AvatarStyle"
                  onClick={() => setShowPictureLinkField(!showPictureLinkField)}
                />
                <IconButton
                  className="PencilIconStyle"
                  onClick={() => setShowPictureLinkField(!showPictureLinkField)}
                  size="small"
                >
                  <EditIcon color="action" />
                </IconButton>
              </Box>
            </Grid>
            {showPictureLinkField && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  margin="dense"
                  size="small"
                  defaultValue={userInfo?.PictureLink || ""}
                  id="Picture Link"
                  label="Picture Link"
                  variant="outlined"
                  onChange={(e) => handleInputChange(e, setPictureLink)}
                />
              </Grid>
            )}
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                fullWidth
                size="small"
                margin="dense"
                value={firstName}
                onChange={(e) => handleInputChange(e, setFirstName)}
                error={!!fieldErrors.firstName}
                helperText={fieldErrors.firstName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                fullWidth
                size="small"
                margin="dense"
                value={lastName}
                onChange={(e) => handleInputChange(e, setLastName)}
                error={!!fieldErrors.lastName}
                helperText={fieldErrors.lastName}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email"
                fullWidth
                size="small"
                value={email}
                onChange={(e) => handleInputChange(e, setEmail)}
                error={!!fieldErrors.email}
                helperText={fieldErrors.email}
              />
            </Grid>
            <Grid item xs={12}>
            
                <Typography variant="h6" component="h2" textAlign="center">
                  Biography
                </Typography>
                <div className="EditorContainer">
                <Editor
                  editorName="editor"
                  content={userInfo?.Biography}
                  saveContent={handleEditorSave}
                  showSaveButton={false}
                />
              </div>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                  control={
                    <Checkbox
                        checked={isSubscribed}
                        onChange={(e) => setIsSubscribed(e.target.checked)}
                        name="subscribe"
                        color="primary"
                    />
                  }
                  label="Subscribe to email updates"
              />
            </Grid>


            <Grid container justifyContent="center" spacing={2}>
              {socialMediaInfo.map((platform) => (
                <Grid item key={platform.Id} xs="auto">
                  <div
                    onClick={() => handleIconClick(platform.Id)}
                    className="SocialMediaIconContainer"
                  >
                    {IconList[platform.Name.toLowerCase()] &&
                      React.createElement(
                        IconList[platform.Name.toLowerCase()],
                        {
                          className: 'SocialMediaIcon',
                        }
                      )}
                    {!IconList[platform.Name.toLowerCase()] &&
                      React.createElement("div", null, `${platform.Name}`)}
                  </div>
                </Grid>
              ))}
            </Grid>
            <Grid item xs={12}>
              {activeSocialMediaId && (
                <TextField
                  fullWidth
                  label={
                    socialMediaInfo.find(
                      (platform) => platform.Id === activeSocialMediaId
                    )?.Name || "Social Media"
                  }
                  variant="outlined"
                  size="small"
                  margin="dense"
                  value={
                    socialMediaLinks.find(
                      (link) =>
                        link.SocialMediaPlatformId === activeSocialMediaId
                    )?.SocialMediaLink || ""
                  }
                  onChange={(e) =>
                    handleSocialMediaLinkChange(
                      activeSocialMediaId,
                      e.target.value
                    )
                  }
                />
              )}
            </Grid>
          </Grid>
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={2}>
              <div className="SubmitButtonContainer">
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  className="ButtonStyle"
                >
                  Submit
                </Button>
              </div>
            </Grid>
          </Grid>
        </form>
      </Box>
    </div>
  );
};

export default UserInfoForm;
